import { User, AccessibleCompany } from '@/lib/api/common/interfaces/user.interface'
import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { Company } from '@/lib/api/common/interfaces/company.interface'
import {
    SESSION_LIFETIME_MS,
    clearActivity,
    isSessionIdle,
    markActivity,
} from '@/lib/session-activity'
import {
    clearAccessToken,
    getAccessToken,
    setAccessToken,
} from '@/lib/access-token-store'
import { isAiUserType } from '@/lib/auth/restricted-viewer'
import { clearDiary } from '@/lib/ai-user/diary-storage'
import { authStorage, clearTabSessionMark, isTabSession, markTabSessionFor } from '@/lib/auth-tab-session'

interface AuthState {
    user: User
    token: string
    refreshToken: string
    isAuthenticated: boolean
    isMaster: boolean
    isCompany: boolean
    isCustom: boolean
    isEmployee: boolean
    isPartner: boolean
    isProfessional: boolean
    accessibleCompanies: AccessibleCompany[]
}

interface SwitchCompanyPayload {
    token: string
    refreshToken: string
    company: Company
    planModules?: Record<string, string[]>
}

const loadInitialState = (): AuthState => {
    if (typeof window === 'undefined')
        return {
            user: {} as User,
            token: '',
            refreshToken: '',
            isAuthenticated: false,
            isMaster: false,
            isCompany: false,
            isCustom: false,
            isEmployee: false,
            isPartner: false,
            isProfessional: false,
            accessibleCompanies: [],
        }

    try {
        const savedState = authStorage().getItem('atendimento_auth')
        const tokenExpiration = authStorage().getItem('token_expiration')

        if (savedState && tokenExpiration) {
            const parsedState = JSON.parse(savedState)

            // Reabrir o navegador não ressuscita sessão parada: vale o mesmo
            // limite de inatividade da aba aberta. Menos na aba de usuário de IA,
            // que fica aberta sem ninguém clicando (ver `use-idle-logout.ts`).
            if (Date.now() < parseInt(tokenExpiration) && (isTabSession() || !isSessionIdle())) {
                return {
                    ...parsedState,
                    // Token nenhum vem do disco: o access é renovado pelo
                    // cookie no início da aplicação (useSessionBootstrap).
                    token: getAccessToken() || '',
                    refreshToken: '',
                    isAuthenticated: true,
                    isMaster: parsedState.user.userType === 'MASTER',
                    isCompany: parsedState.user.userType === 'COMPANY',
                    isCustom: parsedState.user.userType === 'CUSTOM',
                    isEmployee:
                        parsedState.user.userType === 'EMPLOYEE' ||
                        isAiUserType(parsedState.user.userType),
                    isPartner: parsedState.user.userType === 'PARTNER',
                    isProfessional: parsedState.user.userType === 'PROFESSIONAL',
                    accessibleCompanies: parsedState.accessibleCompanies || [],
                }
            }
        }

        authStorage().removeItem('atendimento_auth')
        authStorage().removeItem('token_expiration')
        if (!isTabSession()) clearActivity()
    } catch (error) {
        console.error('Erro ao carregar estado de autenticação:', error)

        authStorage().removeItem('atendimento_auth')
        authStorage().removeItem('token_expiration')
        if (!isTabSession()) clearActivity()
    }

    return {
        user: {} as User,
        token: '',
        refreshToken: '',
        isAuthenticated: false,
        isMaster: false,
        isCompany: false,
        isCustom: false,
        isEmployee: false,
        isPartner: false,
        isProfessional: false,
        accessibleCompanies: [],
    }
}

const authSlice = createSlice({
    name: 'auth',
    initialState: loadInitialState(),
    reducers: {
        setCredentials: (state, action: PayloadAction<AuthState>) => {
            const { user, token, refreshToken, accessibleCompanies } = action.payload
            state.user = user
            state.token = token
            state.refreshToken = refreshToken
            state.isAuthenticated = true
            state.isMaster = user.userType === 'MASTER'
            state.isCompany = user.userType === 'COMPANY'
            state.isCustom = user.userType === 'CUSTOM'
            state.isEmployee =
                user.userType === 'EMPLOYEE' || isAiUserType(user.userType)
            state.isPartner = user.userType === 'PARTNER'
            state.isProfessional = user.userType === 'PROFESSIONAL'
            state.accessibleCompanies = accessibleCompanies || user.accessibleCompanies || []

            const expirationTime = Date.now() + SESSION_LIFETIME_MS

            // Usuário de IA fica com a sessão nesta aba (o backend já guardou o refresh no
            // cookie dela); os demais, na sessão compartilhada do navegador.
            markTabSessionFor(user.userType)

            // Persistimos só a identidade — token nenhum vai para o disco. O
            // access vive em memória e o refresh, em cookie httpOnly.
            const authData = {
                user,
                isAuthenticated: true,
                isMaster: state.isMaster,
                isCompany: state.isCompany,
                isCustom: state.isCustom,
                isEmployee: state.isEmployee,
                isPartner: state.isPartner,
                isProfessional: state.isProfessional,
                accessibleCompanies: state.accessibleCompanies,
            }

            setAccessToken(token || null)
            authStorage().setItem('atendimento_auth', JSON.stringify(authData))
            authStorage().setItem('token_expiration', expirationTime.toString())
            // Entrar é atividade: zera o relógio de inatividade da visita anterior.
            markActivity(true)
        },
        setAccessibleCompanies: (state, action: PayloadAction<AccessibleCompany[]>) => {
            state.accessibleCompanies = action.payload
            state.user.accessibleCompanies = action.payload

            // Atualizar localStorage
            const savedState = authStorage().getItem('atendimento_auth')
            if (savedState) {
                const authData = JSON.parse(savedState)
                authData.accessibleCompanies = action.payload
                authData.user.accessibleCompanies = action.payload
                authStorage().setItem('atendimento_auth', JSON.stringify(authData))
            }
        },
        switchCompany: (state, action: PayloadAction<SwitchCompanyPayload>) => {
            const { token, refreshToken, company, planModules } = action.payload
            state.token = token
            state.refreshToken = refreshToken
            state.user.companyId = company.id
            state.user.company = company
            state.user.organizationId = (company as any)?.organizationId ?? state.user.organizationId ?? null
            if (planModules) {
                state.user.planModules = planModules
            }

            const expirationTime = Date.now() + SESSION_LIFETIME_MS

            // Deep clone para evitar problemas com Proxy do Immer
            const userCopy = JSON.parse(JSON.stringify(state.user))
            const accessibleCompaniesCopy = JSON.parse(JSON.stringify(state.accessibleCompanies))

            const authData = {
                user: userCopy,
                isAuthenticated: true,
                isMaster: state.isMaster,
                isCompany: state.isCompany,
                isCustom: state.isCustom,
                isEmployee: state.isEmployee,
                isPartner: state.isPartner,
                isProfessional: state.isProfessional,
                accessibleCompanies: accessibleCompaniesCopy,
            }

            setAccessToken(token || null)
            authStorage().setItem('atendimento_auth', JSON.stringify(authData))
            authStorage().setItem('token_expiration', expirationTime.toString())
        },
        /**
         * Renovação silenciosa concluída. O access fica em memória e o refresh
         * novo já veio no cookie httpOnly — nada de token é gravado em disco.
         * O `user` vem junto porque o backend o devolve atualizado na renovação
         * (permissões e plano do momento, não os do login).
         */
        updateTokens: (
            state,
            action: PayloadAction<{ token: string; user?: User }>
        ) => {
            const { token, user } = action.payload
            state.token = token
            state.isAuthenticated = true

            if (user) {
                state.user = { ...state.user, ...user }
            }

            const savedState = authStorage().getItem('atendimento_auth')
            if (savedState) {
                try {
                    const authData = JSON.parse(savedState)
                    if (user) authData.user = { ...authData.user, ...user }
                    authStorage().setItem('atendimento_auth', JSON.stringify(authData))
                    authStorage().setItem(
                        'token_expiration',
                        (Date.now() + SESSION_LIFETIME_MS).toString(),
                    )
                } catch {
                    // ignore parse errors
                }
            }
        },
        logout: state => {
            // Diário do Modo IA: tem nome de paciente, não fica no navegador depois de sair.
            if (isAiUserType(state.user?.userType)) clearDiary(state.user?.id)
            clearAccessToken()
            state.user = {} as User
            state.token = ''
            state.refreshToken = ''
            state.isAuthenticated = false
            state.isMaster = false
            state.isCompany = false
            state.isCustom = false
            state.isEmployee = false
            state.isPartner = false
            state.isProfessional = false
            state.accessibleCompanies = []
            // Aba de usuário de IA limpa só a sessão dela: a sessão normal do navegador (o
            // tema e a atividade, compartilhados) continua com quem a usa nas outras abas.
            if (isTabSession()) {
                authStorage().removeItem('atendimento_auth')
                authStorage().removeItem('token_expiration')
                clearTabSessionMark()
                return
            }
            localStorage.removeItem('atendimento_auth')
            localStorage.removeItem('token_expiration')
            // O tema do menu volta a ser lido do backend no próximo login.
            localStorage.removeItem('sidebar_theme')
            clearActivity()
        },
    },
})

export const { setCredentials, setAccessibleCompanies, switchCompany, logout, updateTokens } = authSlice.actions
export default authSlice.reducer
